import { useQuery } from '@tanstack/react-query';
import ChessRestClient from '@/Api/ChessRestClient';
import { getLocalStorage } from '@/Utils/CommonUtils';

export interface RenewalResponse {
  user: {
    id: number;
    name: string;
    mobile: string;
  };
  current_subscription: {
    order_id: string;
    tournament_id: number;
    plan_type: string;
    coach_type?: string;
    start_date: string;
    end_date: string;
    days_to_expiry: number;
    sessions_left: number;
    class_days?: string | null;  // Format: "2,4" (comma-separated weekday numbers, where 1=Monday, 7=Sunday)
    class_timing?: string | null;  // Format: "7:00pm,11:00am" (comma-separated times in IST)
  };
  pricing: Array<{
    id: number;
    // New dimension fields
    level_slug: string | null;
    level_name: string | null;
    format_slug: string | null;
    format_name: string | null;
    coach_tier_code: string | null;
    coach_tier_name: string | null;
    country_iso_code: string;
    max_price_per_session: string | null;
    price_per_session: string | null;
    currency_code: string;
    tournament_id: number | null;
    tournament_name: string | null;
    max_months?: number;
    roadmaps?: Array<{
      duration_months: number;
      total_price: string;
      max_total_price?: string;  // Display price (MRP) for modal
      monthly_equivalent: string;
      max_monthly_equivalent?: string;  // Display monthly equivalent
      incomplete: boolean;
      remaining_months: number;
      segments: Array<{
        pricing_id: number;
        level_slug: string | null;
        level_name: string | null;
        format_slug: string | null;
        format_name: string | null;
        coach_tier_code: string | null;
        coach_tier_name: string | null;
        tournament_id: number | null;
        months_used: number;
        price_per_session: string;
        max_price_per_session?: string;
        segment_total: string;
        max_segment_total?: string;  // Display price (MRP) for modal
        currency_code: string;
      }>;
    }>;
    next_level_pricing_ids: number[];
    webinar_options?: Array<{
      batch_code: string;
      level: string;
      tournament_id: number;
      webinar_ids: number[];
      class_days: string[];
      unique_timings: string[];
    }>;
    is_active: boolean;
    hide_for_user: boolean;
    // Legacy fields for backward compatibility
    plan_type: string;
    currency: string;
    per_session_price: string | null;
    // Perks and additional details
    plan_perks: string[];
    coach_perks: string[];
    // Mark if this is the recommended next level
    is_recommended: boolean;
  }>;
  coupon: {
    code: string;
    discount_type: string;
    discount_value: string;
    currency: string;
    start_at: string;
    expires_at: string;
  } | null;
  placeholders: Array<any>;
  switch_paths: Array<any>;
  banner: {
    should_show: boolean;
    eligible_from: string;
  };
  available_durations?: number[];
  duration_discounts?: {
    [tournamentId: string]: {
      [durationMonths: number]: number;  // discount percentage
    };
  };
}

export default function useGetRenewalForUser(
  enabled: boolean = true,
  // Identifies the actual Renewal page as the caller. The backend only logs/alerts/
  // raises ops+tech tickets for broken pricing when this is set to 'renewal_page' —
  // other callers (e.g. the top banner hook, which polls this on nearly every tab)
  // must NOT pass this, so they don't trigger false alerts for users who are nowhere
  // near their renewal window and never see anything renewal-related on screen.
  source?: 'renewal_page',
) {
  return useQuery<RenewalResponse>(
    ['renewal', source ?? null],
    async () => {
      // Real API call - user ID will be extracted from auth token on backend
      const token = await getLocalStorage('access_token');
      if (!token) {
        throw new Error('Authentication token not found');
      }

      const response = await ChessRestClient.get<RenewalResponse>(
        `subscription/v1/get-renewal-for-user/`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: 'application/json',
          },
          params: source ? {source} : undefined,
        },
      );
      return response.data;
    },
    {
      enabled,
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 10 * 60 * 1000, // 10 minutes
    },
  );
}

